import { useEffect, useState } from 'react';
import { Button } from '@/ui-kit/atoms/button';
import { Logo } from '@/ui-kit/atoms/logo';
import { LoadingSpinner } from '@/ui-kit/atoms/loading-spinner';
import { Card } from '@/ui-kit/molecules/card';

/**
 * sessionStorage flag marking that we've already bounced through interactive
 * re-auth once. Cleared by AppShell on a successful `/api/me`. Exported so the
 * shell can reset it from the success path.
 */
export const REAUTH_FLAG = 'jpc.reauth-attempted';

/**
 * Shown when `/api/me` returns 401 — the access token expired or couldn't be
 * acquired silently. This is a *lapsed session*, NOT a missing-role state (that
 * is `NoAccessPage`). An assigned user whose session timed out lands here, so
 * we escalate to an interactive re-auth instead of telling them they have no
 * access. With a live Entra session `acquireTokenRedirect` round-trips silently
 * and drops the user back where they were.
 *
 * One-shot guard: if we already redirected through interactive auth and still
 * came back with a 401, auto-redirecting again would loop — so we fall back to
 * a manual "Sign in" button.
 */
export function SessionExpiredPage({
  onReauth,
  onSignOut,
}: {
  onReauth: () => void;
  onSignOut: () => void;
}) {
  // Read the guard once at mount: '1' means we already tried an auto-redirect.
  const [looped] = useState(() => sessionStorage.getItem(REAUTH_FLAG) === '1');

  useEffect(() => {
    if (looped) return; // already attempted once — don't loop
    sessionStorage.setItem(REAUTH_FLAG, '1');
    onReauth();
  }, [looped, onReauth]);

  // First pass: auto re-auth in flight — full-page redirect is imminent.
  if (!looped) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-3 text-sm text-gray-600">
            Session expired — signing you back in…
          </p>
        </div>
      </div>
    );
  }

  // Came back still unauthenticated — offer a manual retry rather than looping.
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <div className="text-center">
          <Logo size="lg" />
        </div>
        <h1 className="mt-6 text-center text-xl font-semibold text-gray-900">
          Session expired
        </h1>
        <p className="mt-3 text-center text-sm text-gray-600">
          Your session has expired and we couldn't refresh it automatically.
          Sign in again to continue.
        </p>
        <div className="mt-6 flex justify-center gap-2">
          <Button
            onClick={() => {
              sessionStorage.removeItem(REAUTH_FLAG);
              onReauth();
            }}
          >
            Sign in
          </Button>
          <Button variant="secondary" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </Card>
    </div>
  );
}
